import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, apiJson } from '../apiClient';
import { exportBrickCustomerDocPdf, customerDocTypeLabel } from '../utils/brickCustomerDocExport';
import './BrickFactoryPage.css';

const DOC_TYPES = [
  { value: 'quotation', label: 'Quotation' },
  { value: 'sales_order', label: 'Sales order' },
  { value: 'proforma', label: 'Proforma invoice' },
  { value: 'invoice', label: 'Tax invoice' },
];

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatTzs(v) {
  if (v == null) return 'â€”';
  return Number(v).toLocaleString('en-TZ', { maximumFractionDigits: 0 });
}

const BrickCustomerDocsPage = ({ onNavigate }) => {
  const [types, setTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const [form, setForm] = useState({
    documentType: 'quotation',
    documentDate: todayKey(),
    customerName: '',
    customerPhone: '',
    destination: '',
    brickTypeId: '',
    quantity: '',
    unitPriceTzs: '',
    notes: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, d] = await Promise.all([
        apiJson('/brick/types'),
        apiJson('/brick/customer-documents'),
      ]);
      setTypes((t.types || []).filter((x) => x.isActive !== false));
      setDocuments(d.documents || []);
      setMsg(null);
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Load failed.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeTypes = useMemo(() => types, [types]);

  useEffect(() => {
    if (!form.brickTypeId && activeTypes.length) {
      const t = activeTypes[0];
      setForm((f) => ({
        ...f,
        brickTypeId: String(t.id),
        unitPriceTzs: t.defaultSellingPrice != null ? String(t.defaultSellingPrice) : '',
      }));
    }
  }, [activeTypes, form.brickTypeId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiFetch('/brick/customer-documents', {
        method: 'POST',
        body: JSON.stringify({
          documentType: form.documentType,
          documentDate: form.documentDate,
          customerName: form.customerName.trim(),
          customerPhone: form.customerPhone.trim() || null,
          destination: form.destination.trim() || null,
          notes: form.notes.trim() || null,
          items: [
            {
              brickTypeId: Number(form.brickTypeId),
              quantity: Number(form.quantity),
              unitPriceTzs: form.unitPriceTzs ? Number(form.unitPriceTzs) : null,
            },
          ],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Could not save document.');
      }
      const data = await res.json();
      setDocuments((prev) => [data.document, ...prev]);
      setForm((f) => ({
        ...f,
        customerName: '',
        customerPhone: '',
        destination: '',
        quantity: '',
        notes: '',
      }));
      setMsg({ type: 'success', text: `${customerDocTypeLabel(form.documentType)} saved.` });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="brick-page brick-docs-page">
      <header className="brick-page-header">
        <h1>Customer documents</h1>
        <p className="brick-page-sub">
          Quotation, sales order, proforma invoice, and tax invoice for brick customers. EFD receipt — coming later.
        </p>
        <button type="button" className="brick-link" onClick={() => onNavigate('brick')}>
          ← Dashboard
        </button>
      </header>

      {msg ? (
        <p className={msg.type === 'error' ? 'brick-msg brick-msg--error' : 'brick-msg brick-msg--ok'}>{msg.text}</p>
      ) : null}

      <section className="brick-split">
        <section className="brick-card">
          <h2>New document</h2>
          {!activeTypes.length ? (
            <p className="brick-empty">Add brick types in catalog first.</p>
          ) : (
            <form className="brick-form" onSubmit={handleSubmit}>
              <label>
                Document type
                <select
                  value={form.documentType}
                  onChange={(e) => setForm((f) => ({ ...f, documentType: e.target.value }))}
                >
                  {DOC_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Date
                <input
                  type="date"
                  required
                  value={form.documentDate}
                  onChange={(e) => setForm((f) => ({ ...f, documentDate: e.target.value }))}
                />
              </label>
              <label>
                Customer name
                <input
                  required
                  value={form.customerName}
                  onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
                />
              </label>
              <label>
                Phone
                <input
                  value={form.customerPhone}
                  onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))}
                />
              </label>
              <label>
                Site / destination
                <input
                  value={form.destination}
                  onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))}
                />
              </label>
              <label>
                Brick type
                <select
                  required
                  value={form.brickTypeId}
                  onChange={(e) => {
                    const id = e.target.value;
                    const t = activeTypes.find((x) => String(x.id) === id);
                    setForm((f) => ({
                      ...f,
                      brickTypeId: id,
                      unitPriceTzs:
                        t?.defaultSellingPrice != null ? String(t.defaultSellingPrice) : f.unitPriceTzs,
                    }));
                  }}
                >
                  {activeTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Quantity (pieces)
                <input
                  type="number"
                  min="1"
                  required
                  value={form.quantity}
                  onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                />
              </label>
              <label>
                Unit price (TZS)
                <input
                  type="number"
                  min="0"
                  value={form.unitPriceTzs}
                  onChange={(e) => setForm((f) => ({ ...f, unitPriceTzs: e.target.value }))}
                />
              </label>
              <label>
                Notes
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </label>
              <button type="submit" className="brick-btn brick-btn--primary" disabled={busy}>
                Save document
              </button>
            </form>
          )}
        </section>

        <section className="brick-card brick-card--grow">
          <h2>Recent documents</h2>
          {loading ? <p className="brick-loading">Loading…</p> : null}
          {!loading && !documents.length ? <p className="brick-empty">No documents yet.</p> : null}
          {documents.length ? (
            <section className="brick-table-wrap">
              <table className="brick-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Pcs</th>
                    <th>Total</th>
                    <th>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td>{doc.documentNumber}</td>
                      <td>{customerDocTypeLabel(doc.documentType)}</td>
                      <td>{doc.documentDate}</td>
                      <td>{doc.customerName}</td>
                      <td>{doc.totalPieces}</td>
                      <td>{formatTzs(doc.totalTzs)}</td>
                      <td>
                        <button
                          type="button"
                          className="brick-link"
                          onClick={() => void exportBrickCustomerDocPdf(doc)}
                        >
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}
        </section>
      </section>
    </section>
  );
};

export default BrickCustomerDocsPage;

